import { type FormEvent, useCallback, useEffect, useState } from "react";
import { authFetchJson } from "@/lib/api";
import { Pagination } from "@/components/Pagination";

type Organization = {
  id: string;
  name: string;
};

type Department = {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  settings?: Record<string, unknown>;
  created_at?: string;
  organizations?: { name: string };
};

export function AdminDepartmentsTab() {
  const [depts, setDepts] = useState<Department[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newName, setNewName] = useState("");
  const [newOrgId, setNewOrgId] = useState("");
  const [creating, setCreating] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    setPage(0);
  }, [pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [deptResp, orgResp] = await Promise.all([
        authFetchJson<{ departments: Department[] }>("/api/admin/departments"),
        authFetchJson<{ organizations: Organization[] }>("/api/admin/organizations"),
      ]);
      setDepts(deptResp.departments ?? []);
      const orgList = orgResp.organizations ?? [];
      setOrgs(orgList);
      if (orgList.length > 0 && !newOrgId) {
        setNewOrgId(orgList[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, [newOrgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const createDept = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const name = newName.trim();
      if (!name || !newOrgId) return;
      setCreating(true);
      setError("");
      try {
        await authFetchJson(`/api/organizations/${newOrgId}/departments`, {
          method: "POST",
          body: JSON.stringify({ name }),
        });
        setNewName("");
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Aanmaken mislukt.");
      } finally {
        setCreating(false);
      }
    },
    [newName, newOrgId, load],
  );

  const copyId = useCallback(async (id: string) => {
    await navigator.clipboard.writeText(id);
  }, []);

  const totalPages = Math.max(1, Math.ceil(depts.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedDepts = depts.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  return (
    <div className="space-y-4">
      <form
        onSubmit={createDept}
        className="flex flex-wrap items-end gap-2 rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white p-4 shadow-sm"
      >
        <div className="min-w-0 flex-1">
          <label className="text-xs font-medium text-[var(--cs-navy)]" htmlFor="new-dept-org">
            Organisatie
          </label>
          <select
            id="new-dept-org"
            value={newOrgId}
            onChange={(e) => setNewOrgId(e.target.value)}
            className="mt-1 w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
          >
            {orgs.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-0 flex-1">
          <label className="text-xs font-medium text-[var(--cs-navy)]" htmlFor="new-dept-name">
            Naam
          </label>
          <input
            id="new-dept-name"
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="bv. Cardiologie"
            className="mt-1 w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
          />
        </div>
        <button
          type="submit"
          disabled={creating || !newName.trim() || !newOrgId}
          className="rounded-xl bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-50"
        >
          {creating ? "Aanmaken…" : "Aanmaken"}
        </button>
      </form>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>
      ) : depts.length === 0 ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Nog geen afdelingen.</p>
      ) : (
        <>
        <div className="overflow-x-auto rounded-2xl border border-[rgba(10,52,96,0.10)] glass-card">
          <table className="w-full text-sm">
            <thead className="bg-[var(--cs-ivory)]">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                <th className="px-4 py-2">Naam</th>
                <th className="px-4 py-2">Slug</th>
                <th className="px-4 py-2">Organisatie</th>
                <th className="px-4 py-2">ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
              {pagedDepts.map((dept) => (
                <tr key={dept.id} className="text-xs">
                  <td className="px-4 py-2 font-medium text-[var(--cs-navy)]">{dept.name}</td>
                  <td className="px-4 py-2 font-mono text-[rgba(10,52,96,0.78)]">{dept.slug}</td>
                  <td className="px-4 py-2 text-[rgba(10,52,96,0.78)]">
                    {dept.organizations?.name ?? dept.organization_id.slice(0, 8) + "…"}
                  </td>
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => void copyId(dept.id)}
                      className="font-mono text-[var(--cs-ink-muted)] hover:text-rose-600"
                      title="Kopieer ID"
                    >
                      {dept.id.slice(0, 8)}…
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          total={depts.length}
          page={currentPage}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
        </>
      )}
    </div>
  );
}
